import React from "react";
import Section from "./Section";

export default function HTML_Paragraphs() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">📝 HTML Paragraphs</h1>

      {/* 🔹 Introduction */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "1.5rem",
          marginBottom: "2rem",
        }}
      >
        <p className="tutorial-intro">
          In HTML, the <strong>&lt;p&gt;</strong> tag defines a{" "}
          <em>paragraph</em>. It is one of the most commonly used tags for
          writing text content on web pages.
        </p>
      </div>

      {/* 🔹 Section — Example of Paragraphs */}
      <Section
        title="🔹 Example of HTML Paragraphs"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML Paragraph Example</title>
  </head>
  <body>
    <h1>HTML Paragraphs Example</h1>
    <p>This is the first paragraph of text.</p>
    <p>This is another paragraph, separate from the first one.</p>
    <p>You can add as many paragraphs as needed for your content.</p>
  </body>
</html>`}
        description="The <p> tag defines a paragraph. Browsers automatically add space (a margin) before and after each paragraph."
      />

      {/* 🔹 Key Points */}
      <ul className="tutorial-list-points">
        <li>
          Each <code>&lt;p&gt;</code> tag starts a new block of text.
        </li>
        <li>Browsers add space between paragraphs automatically.</li>
        <li>
          You can style paragraphs using CSS (e.g., color, font-size, spacing).
        </li>
        <li>
          Avoid using multiple &lt;br&gt; tags for spacing — use &lt;p&gt;
          instead.
        </li>
      </ul>

      {/* 🔹 Section — Line Breaks */}
      <Section
        title="🔸 Using Line Breaks"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML Line Break Example</title>
  </head>
  <body>
    <p>This is the first line.<br />This is the second line after a break.</p>
  </body>
</html>`}
        description="The <br> tag inserts a single line break inside a paragraph, useful for poems, addresses, or small text blocks."
      />

      {/* 🔹 Section — Preformatted Text */}
      <Section
        title="🔸 Preformatted Text"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML Preformatted Text</title>
  </head>
  <body>
    <pre>
This text keeps
both      spaces
and line breaks.
    </pre>
  </body>
</html>`}
        description="The <pre> tag displays text exactly as it is written in the HTML file — keeping spaces and line breaks."
      />

      {/* 🔹 Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>✅ Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>Use &lt;p&gt; for blocks of text, not for spacing.</li>
          <li>Keep each paragraph focused on a single idea for readability.</li>
          <li>
            Use CSS for text alignment, indentation, and spacing (not extra
            tags).
          </li>
          <li>Avoid nesting other block elements inside &lt;p&gt; tags.</li>
        </ul>
      </div>

      {/* 🔹 Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary</h2>
        <ul className="tutorial-list-points">
          <li>The &lt;p&gt; tag defines a paragraph in HTML.</li>
          <li>Browsers add space before and after paragraphs.</li>
          <li>
            Use &lt;br&gt; for line breaks and &lt;pre&gt; for preformatted
            text.
          </li>
          <li>Paragraphs improve readability and page structure.</li>
        </ul>
      </div>
    </div>
  );
}
